import { put, post, del, patch } from '../../../common/axiosClient';
import { ProjectDraw, SaveProjectAnalysisDTO } from '../project.type';
import { BulkEditDrawLineItemsDTO, CreateDrawLineItemDTO, CreateOrUpdateProjectDrawDTO } from './project-draws.type';

export const createProjectDraw = async (projectId: string, createDTO: CreateOrUpdateProjectDrawDTO) => {
  return (await post(`projects/${projectId}/draws/`, createDTO)) as ProjectDraw;
};

export const createProjectDraws = async (projectId: string, createDTO: CreateOrUpdateProjectDrawDTO[]) => {
  return (await post(`projects/${projectId}/draws/bulk`, createDTO)) as ProjectDraw[];
};

export const updateProjectDraw = async (projectId: string, drawId: string, updateDTO: CreateOrUpdateProjectDrawDTO) => {
  return (await put(`projects/${projectId}/draws/${drawId}`, updateDTO)) as ProjectDraw;
};

export const updateProjectDraws = async (projectId: string, updateDTO: CreateOrUpdateProjectDrawDTO[]) => {
  return (await put(`projects/${projectId}/draws/bulk`, updateDTO)) as ProjectDraw[];
};

// Visibility is its own endpoint rather than part of the draw update payload: the backend
// refuses to hide the last visible draw, and a hidden draw is read-only in the UI so it never
// goes through the normal save path.
export const setProjectDrawVisibility = async (projectId: string, drawId: string, isEnabled: boolean) => {
  return (await patch(`projects/${projectId}/draws/${drawId}/visibility`, { isEnabled })) as ProjectDraw;
};

export const deleteProjectDraw = async (projectId: string, drawId: string) => {
  return await del(`projects/${projectId}/draws/${drawId}`);
};

export const deleteProjectDraws = async (projectId: string, drawIds: string[]) => {
  const dto = { drawIds }; // Wrap the drawIds in a DTO
  return await del(`projects/${projectId}/draws/bulk`, {
    headers: {
      'Content-Type': 'application/json',
    },
    data: dto,
  });
};

export const createDrawLineItems = async (projectId: string, drawId: string, lineItems: CreateDrawLineItemDTO[]) => {
  return (await post(`projects/${projectId}/draws/${drawId}/line-items`, lineItems)) as CreateDrawLineItemDTO[];
};

export const createDrawsLineItems = async (projectId: string, data: { drawId: string; lineItems: CreateDrawLineItemDTO[] }[]) => {
  return await post(`projects/${projectId}/draws/line-items/bulk-create`, data);
};

export const updateDrawLineItem = async (projectId: string, drawId: string, drawLineItemId: string, lineItem: CreateDrawLineItemDTO) => {
  return (await put(`projects/${projectId}/draws/${drawId}/line-items/${drawLineItemId}`, lineItem)) as CreateDrawLineItemDTO;
};

export const bulkUpdateDrawLineItems = async (projectId: string, drawId: string, dto: BulkEditDrawLineItemsDTO) => {
  return await put(`projects/${projectId}/draws/${drawId}/line-items/bulk-edit`, dto);
};

export const updateDrawsLineItems = async (projectId: string, data: { drawId: string; lineItems: CreateDrawLineItemDTO[] }[]) => {
  return await post(`projects/${projectId}/draws/line-items/bulk-update`, data);
};

export const deleteDrawLineItem = async (projectId: string, drawId: string, lineItemId: string) => {
  return await del(`projects/${projectId}/draws/${drawId}/line-items/${lineItemId}`);
};

export const bulkDeleteDrawLineItems = async (projectId: string, drawId: string, lineItemIds: string[]) => {
  return await del(`projects/${projectId}/draws/${drawId}/line-items/bulk-delete`, {
    headers: {
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(lineItemIds),
  });
};

export const deleteDrawsLineItems = async (projectId: string, data: { drawId: string; lineItemIds: string[] }[]) => {
  return await post(`projects/${projectId}/draws/line-items/bulk-delete`, data);
};

export const saveProjectAnalysisData = async (projectId: string, data: SaveProjectAnalysisDTO) => {
  return await post(`project/${projectId}/project-analysis`, data);
};
